import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Device, Call } from '@twilio/voice-sdk'
import { unlockAudioPlayback } from '../utils/audioUnlock'
import { Phone, PhoneOff, Loader2, AlertCircle, PhoneCall } from 'lucide-react'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'

type PageState = 'loading' | 'ready' | 'connecting' | 'connected' | 'ended' | 'error'

interface LinkMetadata {
  linkId: string
  displayName: string
  description?: string
  lineFriendUrl?: string
}

/**
 * 公開Web通話ページ
 * 認証不要 — 訪問者がブラウザから通話できるシンプルなページ
 */
export default function WebCallPage() {
  const { linkId } = useParams<{ linkId: string }>()
  const [pageState, setPageState] = useState<PageState>('loading')
  const [linkData, setLinkData] = useState<LinkMetadata | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const [callDuration, setCallDuration] = useState(0)

  const deviceRef = useRef<Device | null>(null)
  const callRef = useRef<Call | null>(null)
  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)

  // リンクメタデータを取得
  useEffect(() => {
    if (!linkId) {
      setPageState('error')
      setErrorMessage('無効なリンクです')
      return
    }

    const fetchLinkData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/public/web-call-links/${linkId}`)
        if (!response.ok) {
          setPageState('error')
          setErrorMessage('このリンクは無効または期限切れです')
          return
        }
        const data: LinkMetadata = await response.json()
        setLinkData(data)
        setPageState('ready')
      } catch {
        setPageState('error')
        setErrorMessage('接続エラーが発生しました')
      }
    }

    fetchLinkData()
  }, [linkId])

  // 通話時間カウンター
  const startDurationTimer = useCallback(() => {
    setCallDuration(0)
    durationTimerRef.current = setInterval(() => {
      setCallDuration(prev => prev + 1)
    }, 1000)
  }, [])

  const stopDurationTimer = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current)
      durationTimerRef.current = null
    }
  }, [])

  // Wake Lock: スマートフォンで通話中に画面が消えて通話が切れるのを防止
  const acquireWakeLock = useCallback(async () => {
    if (!('wakeLock' in navigator)) return
    try {
      wakeLockRef.current = await navigator.wakeLock.request('screen')
      console.log('[WebCallPage] Wake Lock 取得')
    } catch (err) {
      console.warn('[WebCallPage] Wake Lock 取得失敗:', err)
    }
  }, [])

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release()
      wakeLockRef.current = null
      console.log('[WebCallPage] Wake Lock 解放')
    }
  }, [])

  // visibilitychange + pageshow: モバイルでアプリ切り替え後に Wake Lock を再取得
  useEffect(() => {
    const isCallActive = pageState === 'connecting' || pageState === 'connected'
    if (!isCallActive) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[WebCallPage] ページ復帰 — Wake Lock 再取得')
        acquireWakeLock()
      }
    }

    // iOS Safari: BFCache復帰時に visibilitychange が発火しないケースの補完
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted && document.visibilityState === 'visible') {
        console.log('[WebCallPage] pageshow(persisted) — Wake Lock 再取得')
        acquireWakeLock()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('pageshow', handlePageShow)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('pageshow', handlePageShow)
    }
  }, [pageState, acquireWakeLock])

  // クリーンアップ
  useEffect(() => {
    return () => {
      stopDurationTimer()
      releaseWakeLock()
      if (deviceRef.current) {
        deviceRef.current.destroy()
        deviceRef.current = null
      }
    }
  }, [stopDurationTimer, releaseWakeLock])

  // 通話時間フォーマット
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // 発信
  const handleCall = async () => {
    if (!linkId) return

    // iOS Safari: 最初の await より前 (タップの同期文脈) で音声再生を解錠
    unlockAudioPlayback()

    setPageState('connecting')

    try {
      // Twilioトークンを取得
      const tokenResponse = await fetch(`${API_BASE_URL}/api/public/web-call-links/${linkId}/token`, {
        method: 'POST',
      })

      if (!tokenResponse.ok) {
        throw new Error('トークンの取得に失敗しました')
      }

      const { token } = await tokenResponse.json()

      // Twilio Deviceを初期化
      const device = new Device(token, {
        edge: ['tokyo', 'singapore'],
        closeProtection: true,
      })

      deviceRef.current = device
      await device.register()

      // 通話を開始
      const call = await device.connect({
        params: {
          callType: 'web-call-link',
          linkId,
        },
      })

      callRef.current = call

      call.on('accept', () => {
        console.log('[WebCallPage] 通話接続')
        setPageState('connected')
        startDurationTimer()
        acquireWakeLock()
      })

      call.on('disconnect', () => {
        console.log('[WebCallPage] 通話終了')
        setPageState('ended')
        stopDurationTimer()
        releaseWakeLock()
        cleanup()
      })

      call.on('cancel', () => {
        console.log('[WebCallPage] 通話キャンセル')
        setPageState('ended')
        stopDurationTimer()
        releaseWakeLock()
        cleanup()
      })

      call.on('error', (error: { message?: string }) => {
        console.error('[WebCallPage] 通話エラー:', error)
        setPageState('error')
        setErrorMessage(error.message || '通話中にエラーが発生しました')
        stopDurationTimer()
        releaseWakeLock()
        cleanup()
      })
    } catch (error) {
      console.error('[WebCallPage] 発信エラー:', error)
      setPageState('error')
      setErrorMessage(error instanceof Error ? error.message : '発信に失敗しました')
    }
  }

  // 切断
  const handleHangup = () => {
    if (callRef.current) {
      callRef.current.disconnect()
    }
  }

  // リソースクリーンアップ
  const cleanup = () => {
    callRef.current = null
    if (deviceRef.current) {
      deviceRef.current.destroy()
      deviceRef.current = null
    }
  }

  // 再発信
  const handleRetry = () => {
    setCallDuration(0)
    setErrorMessage('')
    setPageState('ready')
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-8 text-center">
        {/* ヘッダー */}
        <div className="mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <PhoneCall className="w-8 h-8 text-blue-600" />
          </div>
          {linkData && (
            <>
              <h1 className="text-xl font-bold text-gray-900">
                {linkData.displayName}
              </h1>
              {linkData.description && (
                <p className="text-sm text-gray-500 mt-2 whitespace-pre-line">
                  {linkData.description}
                </p>
              )}
            </>
          )}
        </div>

        {/* 状態別コンテンツ */}
        {pageState === 'loading' && (
          <div className="py-8">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto" />
            <p className="text-gray-500 mt-3 text-sm">読み込み中...</p>
          </div>
        )}

        {pageState === 'ready' && (
          <div className="py-4">
            <button
              onClick={handleCall}
              className="w-20 h-20 bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center mx-auto transition-all hover:scale-105 active:scale-95 shadow-lg"
            >
              <Phone className="w-8 h-8" />
            </button>
            <p className="text-gray-500 mt-4 text-sm">ボタンを押して通話を開始</p>
          </div>
        )}

        {pageState === 'connecting' && (
          <div className="py-8">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto animate-pulse">
              <Phone className="w-8 h-8 text-yellow-600" />
            </div>
            <p className="text-gray-600 mt-4 font-medium">接続中...</p>
            <p className="text-gray-400 mt-1 text-sm">マイクへのアクセスを許可してください</p>
          </div>
        )}

        {pageState === 'connected' && (
          <div className="py-4">
            <div className="text-3xl font-mono font-bold text-gray-900 mb-6">
              {formatDuration(callDuration)}
            </div>
            <button
              onClick={handleHangup}
              className="w-20 h-20 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center mx-auto transition-all hover:scale-105 active:scale-95 shadow-lg"
            >
              <PhoneOff className="w-8 h-8" />
            </button>
            <p className="text-green-600 mt-4 text-sm font-medium">通話中</p>
          </div>
        )}

        {pageState === 'ended' && (
          <div className="py-4">
            <p className="text-gray-600 mb-2">通話が終了しました</p>
            {callDuration > 0 && (
              <p className="text-gray-400 text-sm mb-6">通話時間: {formatDuration(callDuration)}</p>
            )}
            <button
              onClick={handleRetry}
              className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
            >
              もう一度かける
            </button>
          </div>
        )}

        {pageState === 'error' && (
          <div className="py-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-red-600 font-medium">{errorMessage}</p>
            {linkData && (
              <button
                onClick={handleRetry}
                className="mt-6 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
              >
                再試行
              </button>
            )}
          </div>
        )}

        {/* LINE 友だち追加ボタン */}
        {linkData?.lineFriendUrl && pageState !== 'connecting' && pageState !== 'connected' && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <a href={linkData.lineFriendUrl} target="_blank" rel="noopener noreferrer">
              <img
                src="https://scdn.line-apps.com/n/line_add_friends/btn/ja.png"
                alt="友だち追加"
                height="36"
                className="mx-auto"
              />
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
